import 'core/assets/styles/style.css'
import Loader from 'core/components/common/Loader'
import { Error404 } from 'core/components/error/Error404'
import { Error500 } from 'core/components/error/Error500'
import ErrorHandler from 'core/components/error/ErrorHandler'
import { KEYCLOAK_ROLE_SUPERADMIN } from 'core/constants/role'
import { BACKOFFICE_PATH, ROUTE_PATHS } from 'core/constants/routePath'
import { UserContext } from 'core/contexts/UserContext'
import { IKeycloakUtilisateur } from 'core/interfaces/User'
import { UseKeycloakService } from 'core/packages/KeycloakProvider'
import { Layout } from 'core/pages/Layout'
import store, { persistor } from 'core/store'
import { IaStart } from 'iastart/pages/IaStart'
import { IaStartApps } from 'iastart/pages/IaStartApps'
import { useGetAllClientsQuery, useGetClientQuery } from 'lauria/api/client'
import { ModelesLLMProvider } from 'lauria/contexts/ModelesLLMContext'
import { ApplicationEdition } from 'lauria/pages/applications/ApplicationEdition'
import { LayoutApplication } from 'lauria/pages/applications/LayoutApplication'
import { Dashboard } from 'lauria/pages/Dashboard'
import { ProfileEdition } from 'lauria/pages/profiles/ProfileEdition'
import { Profiles } from 'lauria/pages/profiles/Profiles'
import { Kpi } from 'lauria/pages/prompts/Kpi'
import { PromptEdition } from 'lauria/pages/prompts/PromptEdition'
import { PromptList } from 'lauria/pages/prompts/PromptList'
import { Prompts } from 'lauria/pages/prompts/Prompts'
import { Tags } from 'lauria/pages/Tags'
import { useContext, useEffect } from 'react'
import { Provider } from 'react-redux'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { PersistGate } from 'redux-persist/integration/react'

function App() {
  const kc = UseKeycloakService()
  const { user, setCurrentUser, setClients, updateUser } =
    useContext(UserContext)

  const isLoading = !kc.isInitialized()
  const isSuperAdmin = kc.checkRole(KEYCLOAK_ROLE_SUPERADMIN)

  const { data: allClientsData, isFetching: isAllClientsLoading } =
    useGetAllClientsQuery(undefined, {
      skip: !kc.isInitialized() || !isSuperAdmin,
    })

  const clientId = user?.idClient?.toString()
  const { data: singleClientData, isFetching: isSingleClientLoading } =
    useGetClientQuery(
      { id: clientId! },
      { skip: !kc.isInitialized() || isSuperAdmin || !clientId }
    )

  // Initialisation de l'utilisateur une seule fois au chargement (évite la boucle si idClient reste undefined)
  useEffect(() => {
    if (!kc.isInitialized() || user !== null) return

    const parsedToken = kc.getTokenParsed()
    const availableClients: number[] = (
      parsedToken?.lauria?.['idClient'] ?? []
    ).map(Number)

    const baseUser: IKeycloakUtilisateur = {
      id: parsedToken?.sub,
      email: parsedToken?.email,
      firstName: parsedToken?.given_name,
      lastName: parsedToken?.family_name,
      idClient: availableClients[0],
      tagsUtilisateur: (parsedToken?.lauria?.['tags-utilisateur'] ?? []).map(
        Number
      ),
      tagsAdministrateur: (
        parsedToken?.lauria?.['tags-administrateur'] ?? []
      ).map(Number),
    }

    const storedIdClient = localStorage.getItem('idClient')
    if (storedIdClient) {
      baseUser.idClient = Number(storedIdClient)
    } else {
      // Sélection du client L2D si plusieurs clients
      if (availableClients.length > 1 && allClientsData) {
        const l2dClient = allClientsData.find(
          (client) => client.nomClient === 'L2D'
        )
        if (l2dClient) {
          baseUser.idClient = l2dClient.id
        }
      }
    }

    setCurrentUser(baseUser)
  }, [kc, user, allClientsData])

  // Mise à jour des clients dans le contexte
  useEffect(() => {
    if (!kc.isInitialized()) return

    if (isSuperAdmin && allClientsData) {
      setClients(allClientsData)

      const currentClient = allClientsData.find(
        (client) => client.id === user?.idClient
      )

      // Si l'utilisateur n'a pas encore de client, on applique les règles ici aussi (fallback)
      if (!user?.idClient) {
        const l2dClient = allClientsData.find(
          (client) => client.nomClient === 'L2D'
        )
        updateUser({ idClient: l2dClient?.id ?? allClientsData[0]?.id })
      } else if (!currentClient) {
        // Cas où le client ID existant n'est pas dans la liste reçue (sécurité)
        updateUser({ idClient: allClientsData[0]?.id })
      }
    } else if (!isSuperAdmin && singleClientData) {
      setClients([singleClientData])
    }
  }, [allClientsData, singleClientData, isSuperAdmin, setClients, user, updateUser])

  if (isLoading || isAllClientsLoading || isSingleClientLoading) {
    return (
      <Loader center className="h-screen flex items-center justify-center" />
    )
  }

  const {
    APPLICATION_CREATION,
    APPLICATION_EDITION,
    PROFILES,
    PROFILES_EDIT,
    PROMPTS,
    PROMPT_CREATE,
    PROMPT_EDIT,
    ERROR,
    TAG_ADMIN,
    KPI,
    IASTART,
    IASTART_APP,
  } = ROUTE_PATHS

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ErrorHandler>
          <ModelesLLMProvider>
            <BrowserRouter>
              <Routes>
                <Route path={BACKOFFICE_PATH}>
                  <Route path="" element={<Layout displayProfileIcone />}>
                    <Route path="" element={<Dashboard />} />
                    <Route path={TAG_ADMIN} element={<Tags />} />
                    <Route path={PROFILES} element={<Profiles />} />
                    <Route path={PROFILES_EDIT} element={<ProfileEdition />} />
                    <Route
                      path={APPLICATION_CREATION}
                      element={<ApplicationEdition />}
                    />
                    <Route
                      path={APPLICATION_EDITION}
                      element={<ApplicationEdition />}
                    />
                  </Route>
                  <Route path="" element={<LayoutApplication />}>
                    <Route path={PROMPTS} element={<Prompts />}>
                      <Route index element={<PromptList />} />
                      <Route path={KPI} element={<Kpi />} />
                    </Route>
                    <Route path={PROMPT_EDIT} element={<PromptEdition />} />
                    <Route path={PROMPT_CREATE} element={<PromptEdition />} />
                  </Route>
                </Route>
                <Route path="" element={<Layout />}>
                  <Route path={IASTART} element={<IaStart />} />
                  <Route path={IASTART_APP} element={<IaStartApps />} />
                </Route>
                <Route path="" element={<Layout itemsCenter />}>
                  <Route path={ERROR} element={<Error500 />} />
                  <Route path="*" element={<Error404 />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </ModelesLLMProvider>
        </ErrorHandler>
      </PersistGate>
    </Provider>
  )
}

export default App
